import {
  trackOnOverlay,
  overlayFrameProcs,
  requestMeasurement,
} from "../animation";
import { openItemMenu } from "../itemMenuOpener";
import { camera } from "../camera";
import perf from "../perf";
import * as silhouette from "./silhouette";
import * as stack from "./stack";
import {
  SIDE_LEFT,
  SIDE_RIGHT,
  assignLanes,
  auditResult,
  bearingToKey,
  beginAudit,
  emitLeader,
} from "./leader";
import { devtoolsParams } from "../devtools/params";
import { mapLines } from "../render";

export { SIDE_LEFT, SIDE_RIGHT };

// Callouts: a label for each scope, pinned to the left or right edge of the
// screen, joined back to its bubble on the map by a leader line.
//
// The map is a disc and the viewport is landscape, so the room is at the sides.
// Placement is therefore one column per side, and the interesting problem is
// which callout goes where in that column, and how its leader gets there without
// crossing the map or another leader.
//
// Both come from ordering the column by the scope's *bearing* about the map
// centre rather than by its screen y. Two scopes at the same height but different
// radii sit at different bearings, and y order puts their leaders the wrong way
// round; bearing order is what lets the leaders be plain straight lines that
// cannot cross. See ./leader.js for the routing, ./stack.js for the column solve
// and why it no longer jumps, and ./silhouette.js for the map's outline.

const tracks = [];
const overflowPills = [null, null];

if (typeof window !== "undefined") {
  window.sideTracks = tracks;
  window.overflowPills = overflowPills;
}

const STATE_NOT_CREATED = 0;
const STATE_AWAITING_MEASUREMENT = 1;
const STATE_MEASURED = 2;
const STATE_PLACED = 3;

const overlayMargin = 5;
const sideElementMargin = 3;

const offScreenAllowance = 100;

// A scope has to drift this far past the meridian, in screen pixels, before its
// callout changes sides — and then not again for a while. The map rotates
// continuously (`setPositions` seeds from the clock), so a bare sign test on the
// scope's x makes callouts near the poles flap.
const sideHysteresisPx = 25;
const sideCooldownMs = 2000;

// The column eases towards each new solve rather than snapping, settling in
// about a quarter of a second.
const posYTauMs = 80;

const minCalloutHeight = 8;

// Per-side working set. Sized to the stack solver's limit; a column of 64 is far
// more than a 1000px viewport can seat.
const MAX_SIDE = stack.MAX_STACK_SIZE;

const working = [];
const heights = new Float64Array(MAX_SIDE);
const ideals = new Float64Array(MAX_SIDE);
const offsets = new Float64Array(MAX_SIDE);
const previousY = new Float64Array(MAX_SIDE);
const solvedY = new Float64Array(MAX_SIDE);
const blendedY = new Float64Array(MAX_SIDE);
const keyFrom = new Float64Array(MAX_SIDE);
const keyTo = new Float64Array(MAX_SIDE);
const lanes = new Int32Array(MAX_SIDE);

const pathParts = [];

// One path element per side holds every leader on it as a separate subpath. Two
// nodes and two attribute writes a frame, rather than one of each per callout —
// which matters more than it looks: the WebGL backend caches flattened path
// geometry keyed on the `d` string and clears that cache wholesale when it
// fills, so a per-callout path would evict the whole estate's static geometry
// several times a second.
const sidePaths = [null, null];

// When each side's leader bundle was last routed, and for how many callouts -
// the change detector that lets a side skip re-routing while nothing visible
// has moved.
const sideEmitAt = {};

// Queues the track's size measurement for the next frame's read phase. Guarded on
// the state still being AWAITING_MEASUREMENT so a track destroyed or re-created in
// the meantime is not measured against a stale element.
function measureTrack(track) {
  track.state = STATE_AWAITING_MEASUREMENT;
  requestMeasurement(() => {
    if (track.state != STATE_AWAITING_MEASUREMENT || !track.overlayElement)
      return;
    // prepareFunc lays the element out from its own text metrics, so it has to run
    // here rather than at create time — it reads geometry, and the whole point of
    // this phase is that every such read happens before the frame's writes begin.
    if (track.prepareFunc) track.prepareFunc(track.overlayElement, track.side);
    const sz = track.overlayElement.getBBox();
    track.width = sz.width;
    track.height = sz.height;
    track.state = STATE_MEASURED;
  });
}

// Re-measure a track that is already placed, without taking it out of the layout
// while it waits. Used when its content changes and when it swaps sides — both of
// which used to drop it back to AWAITING_MEASUREMENT, so the callout blinked out
// for a frame every time its summary arrived.
function remeasureTrack(track) {
  if (track.state < STATE_MEASURED || track.remeasurePending) return;

  track.remeasurePending = true;
  requestMeasurement(() => {
    track.remeasurePending = false;
    if (track.state < STATE_MEASURED || !track.overlayElement) return;
    if (track.prepareFunc) track.prepareFunc(track.overlayElement, track.side);
    const sz = track.overlayElement.getBBox();
    track.width = sz.width;
    track.height = sz.height;
  });
}

// Higher is kept when the column overflows. Failures first, then scopes still
// running, then whatever arrived most recently. Ties break on arrival so the
// order is stable frame to frame.
function priorityOf(track) {
  const outcome = track.data && track.data.outcomeEmoji;
  if (outcome === "✖") return 3;
  if (!outcome) return 2;
  return 1;
}

function comparePriority(a, b) {
  const d = priorityOf(b) - priorityOf(a);
  if (d) return d;
  return b.created - a.created;
}

function usable(track) {
  return (
    // A hidden scope keeps its last known position — `processOverlay` stops
    // updating a hidden object's target — so without this its callout stayed in
    // the column with a leader pointing at wherever it used to be.
    track.data &&
    !track.data.hidden &&
    (track.state == STATE_MEASURED || track.state == STATE_PLACED) &&
    Number.isFinite(track.targetX) &&
    Number.isFinite(track.targetY) &&
    Number.isFinite(track.width) &&
    Number.isFinite(track.height)
  );
}

function hide(track) {
  if (track.overlayHolder) track.overlayHolder.attr("display", "none");
}

function assignSide(track, now) {
  const obj = track.data;
  if (!obj) return;

  if (track.side == null) {
    track.side = obj.x < 0 ? SIDE_LEFT : SIDE_RIGHT;
    return;
  }

  const threshold = sideHysteresisPx / (camera.scale || 1);

  let wanted = track.side;
  if (track.side === SIDE_LEFT && obj.x > threshold) wanted = SIDE_RIGHT;
  else if (track.side === SIDE_RIGHT && obj.x < -threshold) wanted = SIDE_LEFT;

  if (wanted === track.side) return;
  if (now < track.sideLockedUntil) return;

  track.side = wanted;
  track.sideLockedUntil = now + sideCooldownMs;

  // The callout's box and its text anchor both mirror, and prepareFunc draws
  // whichever side it is handed — so a side change is a re-measure, not the
  // destroy-and-rebuild it used to be.
  remeasureTrack(track);
}

overlayFrameProcs.sideTrackUpdate = (
  overlay,
  rawBounds,
  leftBottomMargin,
  rightBottomMargin,
  delta
) => {
  if (!camera.scale) return;

  const now = performance.now();

  beginAudit();

  const left = [];
  const right = [];

  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];

    if (!usable(track)) {
      // A track with no position yet is not merely invisible: the column solve
      // works off every entry at once, so letting one through would poison the
      // whole side. Its leader has to go too, or it stays drawn at whatever
      // geometry it last had.
      hide(track);
      track.laidOut = false;
      continue;
    }

    // Cheap insurance; the camera fits the map to the viewport, so a scope this
    // far outside it has usually just been hidden by a filter.
    if (
      track.targetX < rawBounds.x1 - offScreenAllowance ||
      track.targetX > rawBounds.x2 + offScreenAllowance ||
      track.targetY < rawBounds.y1 - offScreenAllowance ||
      track.targetY > rawBounds.y2 + offScreenAllowance
    ) {
      hide(track);
      track.laidOut = false;
      continue;
    }

    assignSide(track, now);
    (track.side === SIDE_LEFT ? left : right).push(track);
  }

  perf.begin("callouts");
  try {
    layoutColumn(overlay, left, SIDE_LEFT, rawBounds, leftBottomMargin, delta);
    layoutColumn(
      overlay,
      right,
      SIDE_RIGHT,
      rawBounds,
      rightBottomMargin,
      delta
    );
  } finally {
    perf.end("callouts");
  }

  if (debugEnabled()) drawSilhouetteOutline();
};

// Draws the map's outline and the clearance the leaders route against, in world
// space under the map itself, so the routing can be checked by eye against what
// it is actually avoiding.
let debugOutline = null;
let debugFlag = null;

function debugEnabled() {
  if (debugFlag === null) {
    debugFlag =
      typeof window !== "undefined" &&
      devtoolsParams().get("leaders") === "debug";
  }
  return debugFlag;
}

function drawSilhouetteOutline() {
  if (!silhouette.isPopulated()) return;

  if (!debugOutline) {
    const lines = mapLines();
    if (!lines) return;
    debugOutline = {
      hull: lines
        .append("path")
        .attr("stroke", "#0bf")
        .attr("stroke-width", 1)
        .attr("fill", "none"),
      clear: lines
        .append("path")
        .attr("stroke", "#f0b")
        .attr("stroke-width", 1)
        .attr("fill", "none"),
    };
  }

  const scale = camera.scale || 1;
  const hull = [];
  const clear = [];

  for (let i = 0; i <= silhouette.BUCKETS; i++) {
    const theta =
      ((i % silhouette.BUCKETS) + 0.5) * ((Math.PI * 2) / silhouette.BUCKETS) -
      Math.PI;
    const r = silhouette.worldRadiusAt(theta);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    hull.push(i ? "L" : "M", (cos * r).toFixed(1), (sin * r).toFixed(1));
    const c = r + 8 / scale;
    clear.push(i ? "L" : "M", (cos * c).toFixed(1), (sin * c).toFixed(1));
  }

  debugOutline.hull.attr("d", hull.join(" ") + " Z");
  debugOutline.clear.attr("d", clear.join(" ") + " Z");
}

if (typeof window !== "undefined") {
  // Walks the routing's own record of the last frame: every sampled point past
  // the escape ramp should have ended up outside the map's outline. Non-zero
  // means a leader is drawing across the map — the one thing this layout is
  // supposed to make impossible.
  window.checkLeaders = () => {
    const result = auditResult();
    if (!result.samples) console.log("[leaders] no leaders drawn this frame");
    else if (!result.violations)
      console.log(`[leaders] ${result.samples} samples, 0 violations`);
    else
      console.warn(
        `[leaders] ${result.violations}/${
          result.samples
        } samples inside the map, worst ${result.worstPx.toFixed(1)}px`
      );
    return result;
  };
}

function layoutColumn(overlay, column, side, rawBounds, bottomMargin, delta) {
  const path = ensureSidePath(overlay, side);

  if (!column.length || !silhouette.isPopulated()) {
    for (let i = 0; i < column.length; i++) {
      hide(column[i]);
      column[i].laidOut = false;
    }
    setOverflowPill(overlay, side, 0, 0, rawBounds);
    if (path.lastD !== "") {
      path.lastD = "";
      path.attr("d", "");
    }
    return;
  }

  const ymin = rawBounds.y1 + overlayMargin;
  const ymax = rawBounds.y2 - overlayMargin - bottomMargin;
  const range = ymax - ymin;

  // Which callouts fit. Capacity is a height, not a count — the callouts are all
  // different widths and can be different heights — so take them in priority
  // order until the budget runs out.
  column.sort(comparePriority);

  let budget = range;
  let kept = 0;
  for (let i = 0; i < column.length && kept < MAX_SIDE; i++) {
    const h = Math.max(column[i].height, minCalloutHeight);
    const need = h + (kept ? sideElementMargin : 0);
    if (need > budget) break;
    budget -= need;
    kept++;
  }

  let dropped = column.length - kept;

  // Overflow gets one pill at the foot of the column saying how many are not
  // shown, which has to be seated out of the same budget.
  const pillHeight = dropped > 0 ? overflowPillHeight() : 0;
  if (dropped > 0) {
    while (kept > 0 && pillHeight + sideElementMargin > budget) {
      kept--;
      const h = Math.max(column[kept].height, minCalloutHeight);
      budget += h + (kept ? sideElementMargin : 0);
    }
    dropped = column.length - kept;
  }

  // The solve has to be told about that reservation too. Taking it out of the
  // budget decides how many callouts are kept, but the solver spreads whatever
  // it is given over the whole range it is handed — so without this the column
  // fills to the bottom and the pill is placed on top of the last callout.
  const stackYmax = ymax - (dropped > 0 ? pillHeight + sideElementMargin : 0);

  for (let i = kept; i < column.length; i++) {
    hide(column[i]);
    column[i].laidOut = false;
  }

  working.length = 0;
  for (let i = 0; i < kept; i++) working.push(column[i]);

  if (!working.length) {
    setOverflowPill(overlay, side, dropped, 0, rawBounds);
    if (path.lastD !== "") {
      path.lastD = "";
      path.attr("d", "");
    }
    return;
  }

  const cx = silhouette.centreX();
  const cy = silhouette.centreY();

  const edgeX =
    side === SIDE_LEFT
      ? rawBounds.x1 + overlayMargin
      : rawBounds.x2 - overlayMargin;

  // The order the callouts go down the column, and the whole basis for the
  // leaders not crossing: straight segments between two sequences matched in
  // order do not intersect.
  //
  // Bearing about the map centre, because the scopes sit on a disc and that is
  // the order they appear around its rim — which is exactly the order a fan out
  // to a line of slots needs. Ordering by the direction they lie in as seen from
  // the column instead sounds like it should handle a tight cluster of scopes
  // better; measured against live data it is far worse, 7 to 61 crossings a side
  // against 1.
  for (let i = 0; i < working.length; i++) {
    const track = working[i];
    track.bearing = Math.atan2(track.data.y, track.data.x);
    track.key = bearingToKey(track.bearing, side);
  }
  working.sort((a, b) => a.key - b.key || a.created - b.created);

  // Preferred height is simply the scope's own, so a callout sits level with the
  // thing it labels.
  //
  // Projecting the scope's ray out to the column instead sounds better and is
  // much worse: the gutter on a wide viewport is far wider than the map is tall,
  // so the projection runs off the top or bottom of the screen for anything more
  // than about twenty degrees off horizontal. Nearly every callout then wants the
  // same clamped extreme and they pile up at the ends of the column. The stack
  // solver enforces the bearing order regardless of what it is seeded with, so
  // the seed only has to be sensible, not clever.
  const n = working.length;
  for (let i = 0; i < n; i++) {
    const track = working[i];
    heights[i] = Math.max(track.height, minCalloutHeight);
    ideals[i] = track.targetY;
  }

  stack.separationOffsets(n, heights, sideElementMargin, offsets);
  stack.solve(n, ideals, heights, ymin, stackYmax, offsets, solvedY);

  // Ease towards the solution rather than snapping to it, but do the easing in
  // the stack solver's own coordinates so that every intermediate frame still
  // satisfies the no-overlap constraints exactly.
  let anySettled = false;
  for (let i = 0; i < n; i++) {
    if (working[i].laidOut) {
      anySettled = true;
      previousY[i] = working[i].posY;
    } else {
      previousY[i] = solvedY[i];
    }
  }

  if (anySettled) {
    const alpha = 1 - Math.exp(-Math.max(delta, 0) / posYTauMs);
    stack.blendTowards(
      n,
      previousY,
      solvedY,
      alpha,
      heights,
      ymin,
      stackYmax,
      offsets,
      blendedY
    );

    // Snap once the ease is within a quarter pixel everywhere: the blend
    // approaches the solution asymptotically, and the tail of that approach
    // re-wrote every callout position and leader for frames nobody could see.
    // The solved positions satisfy the stack constraints, so snapping to them
    // wholesale is always safe.
    let blendRemaining = 0;
    for (let i = 0; i < n; i++) {
      const dy = Math.abs(blendedY[i] - solvedY[i]);
      if (dy > blendRemaining) blendRemaining = dy;
    }
    if (blendRemaining < 0.25)
      for (let i = 0; i < n; i++) blendedY[i] = solvedY[i];
  } else {
    for (let i = 0; i < n; i++) blendedY[i] = solvedY[i];
  }

  pathParts.length = 0;

  for (let i = 0; i < n; i++) {
    const track = working[i];
    track.posY = blendedY[i];
    track.laidOut = true;

    track.overlayHolder.attr("display", null);
    // Quarter-pixel steps, so the written string repeats while the position
    // eases and the write no-ops rather than costing a parse.
    track.overlayHolder.attr(
      "transform",
      `translate(${edgeX},${Math.round(track.posY * 4) / 4})`
    );

    // The lane logic works in bearings about the map centre, because that is what
    // the outline a lane rides is indexed by — not the column ordering above.
    keyFrom[i] = bearingToKey(track.bearing, side);

    // Each leader docks at its own callout's point rather than at a line shared
    // across the side. A shared line is set by the widest label there is, so
    // every shorter callout got a long horizontal run out to it — most of the
    // gutter spent on stubs.
    track.tipX = side === SIDE_LEFT ? edgeX + track.width : edgeX - track.width;
    keyTo[i] = bearingToKey(Math.atan2(track.posY - cy, track.tipX - cx), side);
  }

  for (let i = 0; i < n; i++) {
    const track = working[i];
    const obj = track.data;

    // Start at the edge of the bubble rather than at its centre, so the leader
    // meets the glyph instead of drawing over it.
    track.sourceRadius =
      (Math.hypot(obj.x, obj.y) + bubbleRadiusOf(obj)) * camera.scale;
    track.sourceX = cx + Math.cos(track.bearing) * track.sourceRadius;
    track.sourceY = cy + Math.sin(track.bearing) * track.sourceRadius;
  }

  // The leaders re-route only when something has moved visibly or a beat has
  // passed. The idle turn moves every source a fraction of a pixel a frame,
  // and routing and re-tessellating a side's whole leader bundle thirty times
  // a second was a large share of the frame budget. The floor keeps the slow
  // drift honest; anything faster - a solve, a resize, the camera - moves far
  // enough to re-route immediately.
  const nowMs = performance.now();
  const emitState = sideEmitAt[side] || (sideEmitAt[side] = { t: -1e9, n: -1 });
  let moved = 0;
  for (let i = 0; i < n; i++) {
    const track = working[i];
    const m = Math.max(
      Math.abs(
        track.sourceX - (track._emitSX === undefined ? 1e9 : track._emitSX)
      ),
      Math.abs(track.sourceY - (track._emitSY || 0)),
      Math.abs(track.posY - (track._emitPY || 0)),
      Math.abs(track.tipX - (track._emitTX || 0))
    );
    if (m > moved) moved = m;
  }

  // Different periods per side, so the two bundles never re-route on the same
  // frame - together they were a once-a-quarter-second spike big enough to
  // drop a tick.
  if (
    n !== emitState.n ||
    moved > 1.5 ||
    nowMs - emitState.t > 400 + side * 170
  ) {
    emitState.t = nowMs;
    emitState.n = n;

    assignLanes(n, keyFrom, keyTo, lanes);
    computeDockX(n, side);

    for (let i = 0; i < n; i++) {
      const track = working[i];
      track._emitSX = track.sourceX;
      track._emitSY = track.sourceY;
      track._emitPY = track.posY;
      track._emitTX = track.tipX;

      emitLeader(
        pathParts,
        {
          side,
          sourceBearing: track.bearing,
          sourceRadius: track.sourceRadius,
          dockX: track.dockX,
          tipX: track.tipX,
          posY: track.posY,
          lane: lanes[i],
        },
        rawBounds
      );
    }

    const d = pathParts.join(" ");
    if (path.lastD !== d) {
      path.lastD = d;
      path.attr("d", d);
    }
  }

  setOverflowPill(
    overlay,
    side,
    dropped,
    blendedY[n - 1] + heights[n - 1] * 0.5 + sideElementMargin,
    rawBounds
  );
}

// How far in each leader may come before it turns and runs along its own row.
//
// A leader that docks straight onto its own callout's point will cut through any
// wider callout standing between it and the map — the wider box reaches further
// in, and nothing about the leader knows that. This finds, for each leader, the
// widest box whose row it would actually pass through, and stops it there.
//
// Only the rows it crosses, which is why this is not the shared line it replaced:
// that one was set by the widest label on the whole side, so every shorter
// callout paid for it with a long horizontal run. Here the stub is the difference
// in width to whatever was in the way — nothing at all for most leaders.
function computeDockX(n, side) {
  // Which direction "further from the edge, deeper into the map" is.
  const sign = side === SIDE_LEFT ? 1 : -1;

  for (let i = 0; i < n; i++) {
    const track = working[i];
    let dockX = track.tipX;

    const dy = track.posY - track.sourceY;
    if (Math.abs(dy) > 1e-3) {
      for (let j = 0; j < n; j++) {
        if (j === i) continue;

        const other = working[j];
        // Cannot make things worse than what this leader already clears.
        if (sign * other.tipX <= sign * dockX) continue;

        // Both edges of the row's band, so a leader clipping only its corner is
        // still caught.
        const half = heights[j] * 0.5;
        for (let edge = -1; edge <= 1; edge += 2) {
          const y = other.posY + edge * half;
          const along = (y - track.sourceY) / dy;
          if (along < 0 || along > 1) continue;

          const x = track.sourceX + (track.tipX - track.sourceX) * along;
          if (sign * x < sign * other.tipX) {
            dockX = other.tipX;
            break;
          }
        }
      }
    }

    track.dockX = dockX;
  }
}

function bubbleRadiusOf(obj) {
  // The scope bubble is a stroked arc, so its drawn extent is a little wider
  // than the radius the layout reserves for it.
  return 12 * (obj.zoom || 1);
}

function ensureSidePath(overlay, side) {
  let path = sidePaths[side];
  if (!path) {
    // First child, so leaders paint beneath every callout. A leader docks at its
    // own callout's point, and a neighbouring label may well be wider and stand
    // in the way — passing behind it reads as a wire running under a label,
    // where passing over it reads as a mistake.
    path = overlay
      .addFirstChild("path")
      .attr("stroke", "#999")
      .attr("stroke-width", 0.5)
      .attr("fill", "none");
    path.lastD = null;
    sidePaths[side] = path;
  }
  return path;
}

const overflowPillPadding = 3;
const overflowPillTextHeight = 9;

function overflowPillHeight() {
  return overflowPillTextHeight + overflowPillPadding * 2;
}

function setOverflowPill(overlay, side, count, top, rawBounds) {
  let pill = overflowPills[side];

  if (count <= 0) {
    if (pill) pill.holder.attr("display", "none");
    return;
  }

  if (!pill) {
    const holder = overlay.append("g");
    const box = holder
      .append("path")
      .attr("stroke", "#999")
      .attr("stroke-width", 0.5)
      .attr("fill", "white");
    const text = holder
      .append("text")
      .attr("dominant-baseline", "middle")
      .attr("font-size", "0.7em")
      .attr("fill", "#555");
    pill = { holder, box, text, lastCount: -1, lastSide: -1 };
    overflowPills[side] = pill;
  }

  const height = overflowPillHeight();
  const width = 34;

  if (pill.lastCount !== count || pill.lastSide !== side) {
    pill.lastCount = count;
    pill.lastSide = side;
    pill.text.text("+" + count);
    pill.text.attr("text-anchor", side === SIDE_LEFT ? "start" : "end");
    pill.text.attr(
      "x",
      side === SIDE_LEFT ? overflowPillPadding : -overflowPillPadding
    );
    const w = side === SIDE_LEFT ? width : -width;
    pill.box.attr("d", `M 0 ${-height * 0.5} H ${w} V ${height * 0.5} H 0 Z`);
  }

  const edgeX =
    side === SIDE_LEFT
      ? rawBounds.x1 + overlayMargin
      : rawBounds.x2 - overlayMargin;

  let y = top + height * 0.5;
  const limit = rawBounds.y2 - overlayMargin - height * 0.5;
  if (y > limit) y = limit;

  pill.holder.attr("display", null);
  pill.holder.attr("transform", `translate(${edgeX},${y})`);
}

// A backstop on how many callouts can exist at once, not a display limit. What
// actually decides how many are shown is whether they fit the column, which is
// measured each frame — and whatever does not fit is reported by the overflow
// pill rather than dropped in silence. This only has to sit clear of the largest
// column any viewport could seat, so that the pill is what binds first.
const maxTracks = 200;

// side determined automatically by the position of the seed node.
// measures the item returned from createFunc, and again if sizeUpdated
// is called in frameFunc
export function addSideTrack(
  node,
  data,
  createFunc,
  frameFunc,
  destroyFunc,
  side = null,
  prepareFunc = null
) {
  if (tracks.length >= maxTracks) {
    return;
  }
  const track = {
    node,
    data,
    createFunc,
    frameFunc,
    destroyFunc,
    prepareFunc,
    overlayHolder: null,
    overlayElement: null,
    side,
    targetX: null,
    targetY: null,
    posY: null,
    width: null,
    height: null,
    state: STATE_NOT_CREATED,
    // Placement carries over between frames so it can be eased rather than
    // recomputed from nothing; `laidOut` says whether posY means anything yet.
    laidOut: false,
    bearing: 0,
    key: 0,
    created: performance.now(),
    sideLockedUntil: 0,
    remeasurePending: false,
  };
  tracks.push(track);

  trackOnOverlay(
    node,
    (overlay, x, y, bounds) => {
      track.targetX = x;
      track.targetY = y;
      switch (track.state) {
        case STATE_NOT_CREATED:
          if (track.side == null) {
            track.side =
              x < (bounds.x1 + bounds.x2) / 2 ? SIDE_LEFT : SIDE_RIGHT;
          }
          // Measurement now happens in a later frame's read phase, so the holder
          // would otherwise be painted untransformed at the overlay origin in the
          // meantime. sideTrackUpdate clears this as soon as it places the track.
          track.overlayHolder = overlay.append("g").attr("display", "none");
          track.overlayElement = track.createFunc(
            track.data,
            track.overlayHolder,
            track.side
          );
          track.overlayElement.addEventListener("contextmenu", (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            const x = evt.currentTarget.scrollLeft + evt.clientX;
            const y = evt.currentTarget.scrollTop + evt.clientY;
            openItemMenu(node, x, y);
            return false;
          });
          measureTrack(track);
          break;

        case STATE_AWAITING_MEASUREMENT:
          // Measured in the read phase at the top of a later frame; the holder is
          // not placed until then, so it stays invisible in the meantime.
          break;
        case STATE_MEASURED:
          break;
        case STATE_PLACED:
          track.frameFunc(
            track.data,
            track.overlayElement,
            track.targetX,
            track.targetY,
            bounds,
            track.side,
            () => {
              remeasureTrack(track);
            }
          );
          break;
      }
    },
    () => {
      track.destroyFunc(track.data, track.overlayElement);
      if (track.state != STATE_NOT_CREATED) {
        track.overlayHolder.remove();
        track.state = STATE_NOT_CREATED;
      }
      track.laidOut = false;

      // Outside the state check: a track whose node is removed before it was ever
      // created would otherwise stay in the array for the lifetime of the page,
      // and every frame's layout would walk it.
      const idx = tracks.indexOf(track);
      if (idx >= 0) tracks.splice(idx, 1);
    },
    data
  );

  return track;
}
